import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, X, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TermsModal = () => {
  const { requiresTerms, acceptTerms, isLoading } = useAuth();
  const [hasReadTerms, setHasReadTerms] = useState(false);

  console.log('🎭 TermsModal render - requiresTerms:', requiresTerms);

  const handleAcceptTerms = async () => {
    if (!hasReadTerms) {
      return;
    }
    await acceptTerms();
  };

  if (!requiresTerms) {
    return null;
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="w-full max-w-2xl max-h-[90vh] bg-white dark:bg-dark-800 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Términos y Condiciones
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Debes aceptar los términos para continuar
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-96 custom-scrollbar">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <h3 className="text-lg font-semibold mb-4">Acuerdo de Participación - LA FORMULA</h3>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">1. Naturaleza del Sistema</h4>
                  <p>
                    El sistema "LA FORMULA" es una estrategia de apuestas deportivas basada en una
                    progresión exponencial de 13 pasos, enfocada exclusivamente en empates de la liga
                    de fútbol colombiana.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">2. Riesgos Inherentes</h4>
                  <p>
                    <strong>USTED RECONOCE Y ACEPTA</strong> que:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Las apuestas deportivas implican riesgo de pérdida total del capital invertido</li>
                    <li>La fórmula tiene un límite máximo de 13 pasos</li>
                    <li>Si no ocurre un empate antes del paso 13, perderá todo el capital del ciclo</li>
                    <li>Los resultados pasados no garantizan resultados futuros</li>
                    <li>Debe apostar únicamente dinero que puede permitirse perder</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">3. Responsabilidades del Cliente</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Evaluar su capacidad financiera antes de participar</li>
                    <li>Determinar su límite de riesgo personal</li>
                    <li>No participar con dinero destinado a gastos esenciales</li>
                    <li>Entender completamente la mecánica de la fórmula</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">4. Limitación de Responsabilidad</h4>
                  <p>
                    Los administradores de la plataforma actúan únicamente como gestores operativos.
                    <strong>NO SOMOS RESPONSABLES</strong> por:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Pérdidas financieras derivadas de la aplicación de la fórmula</li>
                    <li>Resultados de partidos de fútbol</li>
                    <li>Cambios en las cuotas de apuestas</li>
                    <li>Decisiones de inversión del cliente</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">5. Modelo de Negocio</h4>
                  <p>
                    Al finalizar cada evento/liga:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Su capital inicial se devuelve íntegramente</li>
                    <li>Las ganancias netas se dividen 50/50 entre usted y los administradores</li>
                    <li>No hay comisiones adicionales durante la operación</li>
                  </ul>
                </div>

                <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4">
                  <p className="text-warning-800 dark:text-warning-200 font-medium">
                    <strong>ADVERTENCIA:</strong> Este sistema puede resultar en la pérdida total
                    de su inversión. Participe únicamente si comprende y acepta completamente estos riesgos.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
            <div className="space-y-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasReadTerms}
                  onChange={(e) => setHasReadTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  He leído, entendido y acepto todos los términos y condiciones descritos anteriormente.
                  Comprendo los riesgos involucrados y confirmo que estoy participando voluntariamente
                  con dinero que puedo permitirme perder.
                </span>
              </label>

              <div className="flex space-x-3">
                <button
                  onClick={handleAcceptTerms}
                  disabled={!hasReadTerms || isLoading}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="spinner w-4 h-4"></div>
                      <span>Procesando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Acepto los Términos</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TermsModal;